import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, EventEmitter, Input, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';

/** Shared collection tail: observe the visible button, with explicit retry and keyboard fallback. */
@Component({
  selector: 'app-data-list-load-more',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div *ngIf="error" class="dl-status br-text-danger" role="alert">
      {{ error }}
      <button type="button" class="br-btn br-btn--outline-danger br-btn--sm" (click)="retry.emit()">Retry</button>
    </div>
    <p *ngIf="loading && showLoading" class="dl-status br-text-muted" role="status">Loading…</p>
    <ng-container *ngIf="!loading && !error && empty">
      <ng-container *ngIf="emptyTemplate; else defaultEmpty" [ngTemplateOutlet]="emptyTemplate"></ng-container>
      <ng-template #defaultEmpty><p class="dl-status br-text-muted">{{ emptyText }}</p></ng-template>
    </ng-container>
    <div *ngIf="hasMore && !loading && !error" class="dl-status">
      <button #moreButton type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
              (click)="more.emit()">Load more</button>
    </div>
  `,
  styles: [':host { display: block; }', '.dl-status { padding: var(--br-space-3); margin: 0; }'],
})
export class DataListLoadMoreComponent implements AfterViewChecked, OnDestroy {
  @Input() hasMore = false;
  @Input() loading = false;
  @Input() showLoading = true;
  @Input() emptyTemplate?: TemplateRef<unknown>;
  @Input() error = '';
  @Input() empty = false;
  @Input() emptyText = 'Nothing here yet.';
  @Input() autoLoadMore = true;
  @Input() progress = 0;
  @Output() more = new EventEmitter<void>();
  @Output() retry = new EventEmitter<void>();
  @ViewChild('moreButton') moreButton?: { nativeElement: HTMLElement };
  private observer?: IntersectionObserver;
  private observedButton?: HTMLElement;
  private observedProgress = -1;

  ngAfterViewChecked(): void {
    const button = this.autoLoadMore && !this.loading && !this.error ? this.moreButton?.nativeElement : undefined;
    if (button === this.observedButton && this.progress === this.observedProgress) return;
    this.observer?.disconnect();
    this.observer = undefined;
    this.observedButton = button;
    this.observedProgress = this.progress;
    if (!button) return;
    // The viewport root respects every clipping ancestor, including sideways board scrolling.
    const observer = new IntersectionObserver(entries => {
      if (this.observer === observer && this.autoLoadMore && !this.loading && !this.error && this.hasMore
          && entries.some(entry => entry.isIntersecting)) this.more.emit();
    });
    this.observer = observer;
    observer.observe(button);
  }

  ngOnDestroy(): void { this.observer?.disconnect(); this.observer = undefined; }
}
